import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { AppStateProvider, USE_MOCK_DATA, useAppContext, RoleType } from "./lib/mockState";
import { useT } from "./lib/i18n";
import { Shell } from "./components/layout/Shell";
import { Sidebar } from "./components/layout/Sidebar";
import { PersistentChatPanel } from "./components/ai-counsel/PersistentChatPanel";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { FirmProvider, useFirm } from "./contexts/FirmContext";
import { billingRecoverySearch, isBillingRecoveryPath, resolveSubscriptionAccess } from "./lib/subscriptionAccess";
import { createCaseCallable, createContractCallable } from "./lib/matterCallables";
import {
  createCustomMatterReminderCallable,
  createMatterCalendarEventCallable,
  resolveMatterAlertCallable,
  updateMatterEventActionCallable,
} from "./lib/phase67Callables";

import { GlobalSearch } from "./components/layout/GlobalSearch";
import { db } from "./firebase";
import { useContainerTier, useViewportTier } from "./components/layout/responsive";
import { CasePage } from "./routes/matters/case/page";
import { PortalOperationsPage } from "./routes/portal-ops/page";
import { AgentWorkspacePage } from "./pages/AgentWorkspace";

function lazyWithRetry<T extends React.ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
  retryKey: string,
) {
  return lazy(async () => {
    try {
      const loaded = await importer();
      if (typeof window !== "undefined") {
        window.sessionStorage.removeItem(retryKey);
      }
      return loaded;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      const chunkLoadFailure = /ChunkLoadError|Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed/i.test(message);

      if (chunkLoadFailure && typeof window !== "undefined") {
        const hasRetried = window.sessionStorage.getItem(retryKey) === "1";
        if (!hasRetried) {
          window.sessionStorage.setItem(retryKey, "1");
          window.location.reload();
          return new Promise<never>(() => undefined);
        }
      }

      throw error;
    }
  });
}

const CockpitPage = lazy(() => import("./routes/cockpit/page").then((module) => ({ default: module.CockpitPage })));
const CasesWorkspaceIndexPage = lazy(() => import("./routes/matters/index-page").then((module) => ({ default: module.CasesWorkspaceIndexPage })));
const ContractsWorkspaceIndexPage = lazy(() => import("./routes/matters/index-page").then((module) => ({ default: module.ContractsWorkspaceIndexPage })));
const ContractPage = lazy(() => import("./routes/matters/contract/page").then((module) => ({ default: module.ContractPage })));
const DraftingPage = lazy(() => import("./routes/drafting/page").then((module) => ({ default: module.DraftingPage })));
const MyDayPage = lazy(() => import("./routes/my-day/page").then((module) => ({ default: module.MyDayPage })));
const ClauseLibraryPage = lazyWithRetry(
  () => import("./routes/clause-library/page").then((module) => ({ default: module.ClauseLibraryPage })),
  "lazy-retry:clause-library",
);
const DocumentTemplatesPage = lazy(() => import("./routes/templates/page").then((module) => ({ default: module.DocumentTemplatesPage })));
const KnowledgeBasePage = lazy(() => import("./routes/knowledge-base/page").then((module) => ({ default: module.KnowledgeBasePage })));
const WorkQueuePage = lazy(() => import("./routes/work-queue/page").then((module) => ({ default: module.WorkQueuePage })));
const TimeTimerPage = lazy(() => import("./routes/time-tracking/page").then((module) => ({ default: module.TimeTimerPage })));
const ExportsLibraryPage = lazy(() => import("./routes/exports/page").then((module) => ({ default: module.ExportsLibraryPage })));
const BillingLedgerPage = lazy(() => import("./routes/billing/page").then((module) => ({ default: module.BillingLedgerPage })));
const FirmAnalyticsPage = lazy(() => import("./routes/analytics/page").then((module) => ({ default: module.FirmAnalyticsPage })));
const FacetedSearchPage = lazy(() => import("./routes/search/page").then((module) => ({ default: module.FacetedSearchPage })));
const ClientCommsFeedPage = lazy(() => import("./routes/communications/page").then((module) => ({ default: module.ClientCommsFeedPage })));
const DocumentStoragePage = lazy(() => import("./routes/documents/page").then((module) => ({ default: module.DocumentStoragePage })));
const TrashRecoveryPage = lazy(() => import("./routes/trash/page").then((module) => ({ default: module.TrashRecoveryPage })));
const FirmSettingsPage = lazy(() => import("./routes/settings/page").then((module) => ({ default: module.FirmSettingsPage })));
const SettingsImportPage = lazy(() => import("./routes/settings/import/page").then((module) => ({ default: module.SettingsImportPage })));
const MicrosoftOAuthCallbackPage = lazy(() => import("./routes/settings/microsoft/MicrosoftOAuthCallback").then((module) => ({ default: module.MicrosoftOAuthCallback })));
const PipelineDashboardPage = lazy(() => import("./routes/pipeline/page").then((module) => ({ default: module.PipelineDashboardPage })));
const PipelineLeadDetailPage = lazy(() => import("./routes/pipeline/$leadId").then((module) => ({ default: module.PipelineLeadDetailPage })));
const ClientWorkspacePage = lazy(() => import("./routes/clients/$clientId").then((module) => ({ default: module.ClientWorkspacePage })));
const OwnerCenterReferralsPage = lazy(() => import("./routes/owner-center/referrals/page").then((module) => ({ default: module.OwnerCenterReferralsPage })));
const AdminControlTowerPage = lazy(() => import("./routes/admin/control-tower/page").then((module) => ({ default: module.AdminControlTowerPage })));
const AuthorizedPartiesPage = lazy(() => import("./routes/parties/page").then((module) => ({ default: module.AuthorizedPartiesPage })));
const ClientPortalPublicPage = lazy(() => import("./routes/portal/public-page").then((module) => ({ default: module.ClientPortalPublicPage })));
const PortalActivatePage = lazy(() => import("./routes/portal/activate-page").then((module) => ({ default: module.PortalActivatePage })));
const PortalLoginPage = lazy(() => import("./routes/portal/login-page").then((module) => ({ default: module.PortalLoginPage })));
const PortalDashboardPage = lazy(() => import("./routes/portal/dashboard-page").then((module) => ({ default: module.PortalDashboardPage })));
const BusinessInvitePage = lazy(() => import("./routes/business/invite/page").then((module) => ({ default: module.BusinessInvitePage })));
const BusinessProposalPage = lazy(() => import("./routes/business/proposal/page").then((module) => ({ default: module.BusinessProposalPage })));
const AcceptInvitePage = lazy(() => import("./routes/accept-invite/page").then((module) => ({ default: module.AcceptInvitePage })));
const Phase12PreviewPage = lazy(() => import("./routes/__preview/phase12/page").then((module) => ({ default: module.Phase12PreviewPage })));
const LoginPage = lazy(() => import("./routes/(auth)/login/page").then((module) => ({ default: module.LoginPage })));
const LegalPolicyPage = lazy(() => import("./routes/legal/page").then((module) => ({ default: module.LegalPolicyPage })));
const PublicLandingPage = lazy(() => import("./routes/PublicLandingPage").then((module) => ({ default: module.PublicLandingPage })));
const ContactPage = lazy(() => import("./routes/contact/page").then((module) => ({ default: module.ContactPage })));
const CalendarPage = lazy(() => import("./routes/calendar/page").then((module) => ({ default: module.CalendarPage })));
const AlertsPage = lazy(() => import("./routes/alerts/page").then((module) => ({ default: module.AlertsPage })));
const ClientsPage = lazy(() => import("./routes/clients/page").then((module) => ({ default: module.ClientsPage })));
const TasksPage = lazy(() => import("./routes/tasks/page").then((module) => ({ default: module.TasksPage })));
const FirmBootstrapPage = lazy(() => import("./routes/onboarding/firm-bootstrap/page").then((module) => ({ default: module.FirmBootstrapPage })));
// Wave O / O-3 — Gate-1 firm setup wizard (mandatory before workspace access).
const FirmSetupWizardPage = lazy(() => import("./routes/onboarding/firm-setup/page").then((module) => ({ default: module.FirmSetupWizardPage })));
const USE_LOCAL_PREVIEW_SURFACES = import.meta.env.MODE !== "production" && !import.meta.env.VITE_FIREBASE_API_KEY;

const RouteFallback = () => {
  const t = useT();
  return (
    <div className="flex-1 grid place-items-center p-8 text-xs font-bold text-slate-500">
      {t("shell.loadingWorkspace")}
    </div>
  );
};

const KnowledgeAliasPage = () => {
  const t = useT();
  return (
  <div className="flex-1 overflow-y-auto bg-slate-50/40 p-4">
    <div className="rounded-xl border border-slate-800 bg-slate-900 px-4 py-3 text-white shadow-sm">
      <h1 className="text-sm font-black tracking-tight">{t("shell.knowledge.title")}</h1>
      <p className="mt-1 text-xs text-slate-300">{t("shell.knowledge.desc")}</p>
    </div>
    <section className="mt-4 grid gap-4 md:grid-cols-3">
      {[
        { label: t("shell.knowledge.articles"), value: t("shell.knowledge.articlesVal"), detail: t("shell.knowledge.articlesDetail") },
        { label: t("shell.knowledge.candidate"), value: t("shell.knowledge.candidateVal"), detail: t("shell.knowledge.candidateDetail") },
        { label: t("shell.knowledge.scope"), value: t("shell.knowledge.scopeVal"), detail: t("shell.knowledge.scopeDetail") },
      ].map((item) => (
        <div key={item.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-slate-400">{item.label}</p>
          <p className="mt-2 text-lg font-black text-slate-950">{item.value}</p>
          <p className="mt-2 text-xs text-slate-500">{item.detail}</p>
        </div>
      ))}
    </section>
  </div>
  );
};

const TeamManagementAliasPage = () => {
  const t = useT();
  return (
  <div className="flex-1 overflow-y-auto bg-slate-50/40 p-4">
    <div className="rounded-xl border border-slate-800 bg-slate-900 px-4 py-3 text-white shadow-sm">
      <h1 className="text-sm font-black tracking-tight">{t("shell.team.title")}</h1>
      <p className="mt-1 text-xs text-slate-300">{t("shell.team.desc")}</p>
    </div>
    <section className="mt-4 grid gap-4 md:grid-cols-3">
      {[
        { label: t("shell.team.members"), value: t("shell.team.membersVal"), detail: t("shell.team.membersDetail") },
        { label: t("shell.team.roleMatrix"), value: t("shell.team.roleMatrixVal"), detail: t("shell.team.roleMatrixDetail") },
        { label: t("shell.team.workload"), value: t("shell.team.workloadVal"), detail: t("shell.team.workloadDetail") },
      ].map((item) => (
        <div key={item.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-slate-400">{item.label}</p>
          <p className="mt-2 text-lg font-black text-slate-950">{item.value}</p>
          <p className="mt-2 text-xs text-slate-500">{item.detail}</p>
        </div>
      ))}
    </section>
  </div>
  );
};

const OwnerCenterPage = () => {
  const t = useT();
  return (
  <div className="flex-1 overflow-y-auto bg-slate-50/40 p-4">
    <div className="rounded-xl border border-slate-800 bg-slate-900 px-4 py-3 text-white shadow-sm">
      <h1 className="text-sm font-black tracking-tight">{t("shell.owner.title")}</h1>
      <p className="mt-1 text-xs text-slate-300">{t("shell.owner.desc")}</p>
    </div>
    <section className="mt-4 grid gap-4 md:grid-cols-3">
      {[
        { label: t("shell.owner.revenue"), value: t("shell.owner.revenueVal"), detail: t("shell.owner.revenueDetail") },
        { label: t("shell.owner.utilisation"), value: t("shell.owner.utilisationVal"), detail: t("shell.owner.utilisationDetail") },
        { label: t("shell.owner.portfolio"), value: t("shell.owner.portfolioVal"), detail: t("shell.owner.portfolioDetail") },
      ].map((item) => (
        <div key={item.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-slate-400">{item.label}</p>
          <p className="mt-2 text-lg font-black text-slate-950">{item.value}</p>
          <p className="mt-2 text-xs text-slate-500">{item.detail}</p>
        </div>
      ))}
    </section>
  </div>
  );
};

const ProductionPendingSurface = ({ title }: { title: string }) => {
  const t = useT();
  return (
  <div className="flex-1 grid place-items-center p-8 text-center">
    <div className="max-w-lg rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
      <ShieldCheck className="mx-auto text-orange-600" size={24} />
      <h1 className="mt-4 text-lg font-black text-slate-950">{title}</h1>
      <p className="mt-2 text-sm text-slate-500">
        {t("shell.productionPending.desc")}
      </p>
    </div>
  </div>
  );
};

const SubscriptionRecoveryNotice = ({ statusLabel }: { statusLabel: string }) => {
  const t = useT();
  return (
  <div className="min-h-screen grid place-items-center bg-slate-100 p-6">
    <div className="max-w-xl rounded-2xl border border-orange-200 bg-white p-8 text-center shadow-sm">
      <AlertTriangle className="mx-auto text-orange-600" size={28} />
      <h1 className="mt-4 text-xl font-black text-slate-950">{t("shell.billingRecovery.title")}</h1>
      <p className="mt-2 text-sm text-slate-600">{statusLabel}</p>
      <p className="mt-3 text-sm text-slate-500">{t("shell.billingRecovery.restore")}</p>
    </div>
  </div>
  );
};

const LegacyPortalRedirect = () => {
  const location = useLocation();
  const { token = "", section } = useParams();
  const legacySection = section
    ?? (["activate", "login", "documents", "invoices", "matters", "proposal", "share", "sign"] as const)
      .find((candidate) => location.pathname.startsWith(`/portal/${candidate}/`));

  return <Navigate to={legacySection ? `/portal/${token}/${legacySection}` : `/portal/${token}`} replace />;
};

const AdminSurfaceAliasRedirect = () => {
  const { adminSurface } = useParams<{ adminSurface?: string }>();
  return <Navigate to={adminSurface ? `/admin/control-tower/${adminSurface}` : "/admin/control-tower"} replace />;
};

const AdminSettingsSectionAliasRedirect = () => {
  const { section } = useParams<{ section?: string }>();
  return <Navigate to={section ? `/admin/control-tower/settings/${section}` : "/admin/control-tower/settings/profile"} replace />;
};

const ControlDeploymentRedirect = () => {
  const location = useLocation();
  const targetOrigin = import.meta.env.VITE_CONTROL_CENTER_ORIGIN || "https://control.iamvault.app";
  const nextPath = location.pathname.startsWith("/control") ? location.pathname : "/control/dashboard";

  if (typeof window !== "undefined") {
    const targetUrl = `${targetOrigin}${nextPath}${location.search}${location.hash}`;
    if (targetUrl !== window.location.href) {
      window.location.replace(targetUrl);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-slate-100 p-6 text-center">
      <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <ShieldCheck className="mx-auto text-orange-600" size={24} />
        <h1 className="mt-4 text-lg font-black text-slate-950">Opening Control Center</h1>
        <p className="mt-2 text-sm text-slate-500">Operator access has moved to the dedicated control deployment.</p>
      </div>
    </div>
  );
};

const PUBLIC_LEGAL_PATHS = new Set([
  "/legal",
  "/privacy",
  "/terms",
  "/dpa",
  "/ai-disclaimer",
  "/cookies",
  "/cookie",
  "/acceptable-use",
  "/aup",
  "/security",
  "/refund",
  "/sla",
  "/reliance",
  "/evidence",
  "/third-party-ai",
  "/confidentiality",
  "/governing-law",
  "/accessibility",
  "/contact",
]);

type CreditDepletionState = {
  balance: number | null;
  thresholdBase: number;
  percent: number | null;
  level: "ok" | "thirty" | "ten" | "depleted";
};

function resolveCreditDepletionState(data: Record<string, unknown> | null): CreditDepletionState {
  if (!data) return { balance: null, thresholdBase: 500, percent: null, level: "ok" };
  const balance = typeof data.balance === "number" && Number.isFinite(data.balance) ? data.balance : 0;
  const creditCap = typeof data.creditCap === "number" && data.creditCap > 0 ? data.creditCap : 0;
  const monthlyIncludedCredits = typeof data.monthlyIncludedCredits === "number" && data.monthlyIncludedCredits > 0 ? data.monthlyIncludedCredits : 0;
  const thresholdBase = Math.max(creditCap, monthlyIncludedCredits, 500);
  const percent = Math.max(0, Math.round((balance / thresholdBase) * 100));
  const level = balance <= 0 ? "depleted" : percent <= 10 ? "ten" : percent <= 30 ? "thirty" : "ok";
  return { balance, thresholdBase, percent, level };
}

// Inline styled sub-pages for mock-complete sidebar routing
import { 
  Calendar as CalendarIcon, 
  Flag, 
  Users, 
  CheckSquare, 
  Layers, 
  Settings as SettingsIcon,
  Bot,
  PlusCircle,
  HelpCircle,
  TrendingUp,
  CreditCard,
  Building2,
  Clock,
  ShieldCheck,
  Award,
  Pin,
  ListFilter,
  X,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Check,
  Bell
} from "lucide-react";

// MyDayPage component refactored to routes/my-day/page.tsx

// Sprint 12.4: inline CalendarPage / AlertsPage / ClientsPage / TasksPage
// (and the dead inline SettingsPage) were extracted into
// routes/{calendar,alerts,clients,tasks}/page.tsx and are now lazy-imported
// at the top of this file. The dead inline SettingsPage was removed; the
// /settings route already binds to FirmSettingsPage from routes/settings/page.

// ==========================================================
// Main Application Context Binder (The AppContent multiprocessor)
// ==========================================================
const AppContent: React.FC = () => {
  const { 
    currentRoute, 
    lang,
    role,
    sidebarOpen,
    setSidebarOpen,
    aiCounselOpen,
    setAiCounselOpen,
    aiCounselWidth,
    setAiCounselWidth 
  } = useAppContext();
  const location = useLocation();
  const navigate = useNavigate();
  const isControlSubdomain = typeof window !== "undefined" && window.location.hostname === "control.iamvault.app";
  const isAr = lang === "ar";
  const { user, isAuthReady, needsFirmBootstrap } = useAuth();
  const { firmId, firmReady, subscriptionStatus, trialExpiresAt, firmStatus, firmSuspended, setupComplete } = useFirm();
  const [creditDepletion, setCreditDepletion] = useState<CreditDepletionState>(() => resolveCreditDepletionState(null));
  const shellChromeRef = useRef<HTMLElement | null>(null);
  const viewportTier = useViewportTier();
  const shellTier = useContainerTier(shellChromeRef, viewportTier);
  const isStackTier = shellTier === "stack";
  const isCompactTier = shellTier === "compact";

  useEffect(() => {
    if (!firmId || !user) {
      setCreditDepletion(resolveCreditDepletionState(null));
      return;
    }
    const unsubscribe = onSnapshot(
      doc(db, "firm_credits", firmId),
      (snapshot) => {
        setCreditDepletion(resolveCreditDepletionState(snapshot.exists() ? (snapshot.data() as Record<string, unknown>) : null));
      },
      () => {
        setCreditDepletion(resolveCreditDepletionState(null));
      },
    );
    return unsubscribe;
  }, [firmId, user]);

  const creditReadOnly = creditDepletion.level === "depleted";
  const creditBanner =
    creditDepletion.level === "depleted"
      ? "AI credits are depleted. The workspace is in read-only AI mode until credits are replenished."
      : creditDepletion.level === "ten"
        ? `AI credits are critically low: ${creditDepletion.balance?.toLocaleString() ?? 0} remaining (${creditDepletion.percent ?? 0}%).`
        : creditDepletion.level === "thirty"
          ? `AI credits are below 30%: ${creditDepletion.balance?.toLocaleString() ?? 0} remaining (${creditDepletion.percent ?? 0}%).`
          : null;
  const navigateToBilling = () => navigate("/settings?tab=billing");

  // React Router multiplexer is rendered inline in the return JSX below

  if (location.pathname.startsWith("/portal/") || location.pathname.startsWith("/client-portal/") || location.pathname.startsWith("/p/")) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/client-portal/:token" element={<LegacyPortalRedirect />} />
          <Route path="/client-portal/:token/:section" element={<LegacyPortalRedirect />} />
          <Route path="/p/:token" element={<LegacyPortalRedirect />} />
          <Route path="/portal/activate" element={<PortalActivatePage />} />
          <Route path="/portal/activate/:token" element={<LegacyPortalRedirect />} />
          <Route path="/portal/login" element={<PortalLoginPage />} />
          <Route path="/portal/dashboard" element={<PortalDashboardPage />} />
          <Route path="/portal/login/:token" element={<LegacyPortalRedirect />} />
          <Route path="/portal/documents/:token" element={<LegacyPortalRedirect />} />
          <Route path="/portal/invoices/:token" element={<LegacyPortalRedirect />} />
          <Route path="/portal/matters/:token" element={<LegacyPortalRedirect />} />
          <Route path="/portal/proposal/:token" element={<LegacyPortalRedirect />} />
          <Route path="/portal/share/:token" element={<LegacyPortalRedirect />} />
          <Route path="/portal/sign/:token" element={<LegacyPortalRedirect />} />
          <Route path="/portal/:token" element={<ClientPortalPublicPage />} />
          <Route path="/portal/:token/:section" element={<ClientPortalPublicPage />} />
          <Route path="*" element={<Navigate to="/cockpit" replace />} />
        </Routes>
      </Suspense>
    );
  }

  if (location.pathname.startsWith("/business/invite/") || location.pathname.startsWith("/business/proposal/")) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/business/invite/:token" element={<BusinessInvitePage />} />
          <Route path="/business/proposal/:token" element={<BusinessProposalPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    );
  }

  if (location.pathname.startsWith("/legal/") || PUBLIC_LEGAL_PATHS.has(location.pathname)) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/legal" element={<LegalPolicyPage />} />
          <Route path="/legal/:policy" element={<LegalPolicyPage />} />
          <Route path="/privacy" element={<LegalPolicyPage />} />
          <Route path="/terms" element={<LegalPolicyPage />} />
          <Route path="/dpa" element={<LegalPolicyPage />} />
          <Route path="/ai-disclaimer" element={<LegalPolicyPage />} />
          <Route path="/cookies" element={<LegalPolicyPage />} />
          <Route path="/cookie" element={<LegalPolicyPage />} />
          <Route path="/acceptable-use" element={<LegalPolicyPage />} />
          <Route path="/aup" element={<LegalPolicyPage />} />
          <Route path="/security" element={<LegalPolicyPage />} />
          <Route path="/refund" element={<LegalPolicyPage />} />
          <Route path="/sla" element={<LegalPolicyPage />} />
          <Route path="/reliance" element={<LegalPolicyPage />} />
          <Route path="/evidence" element={<LegalPolicyPage />} />
          <Route path="/third-party-ai" element={<LegalPolicyPage />} />
          <Route path="/confidentiality" element={<LegalPolicyPage />} />
          <Route path="/governing-law" element={<LegalPolicyPage />} />
          <Route path="/accessibility" element={<LegalPolicyPage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Routes>
      </Suspense>
    );
  }

  if (location.pathname.startsWith("/__preview/phase12/")) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/__preview/phase12/:surface" element={<Phase12PreviewPage />} />
          <Route path="*" element={<Navigate to="/__preview/phase12/dashboard" replace />} />
        </Routes>
      </Suspense>
    );
  }

  // Auth gate: while Firebase is still resolving persistence/state, render a neutral splash.
  if (!isAuthReady) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0f172a] text-xs font-bold text-slate-400 font-mono tracking-widest uppercase">
        Resolving session…
      </div>
    );
  }

  if (isControlSubdomain && location.pathname !== "/login") {
    if (!location.pathname.startsWith("/control")) {
      return <Navigate to="/control/dashboard" replace />;
    }
    return <ControlDeploymentRedirect />;
  }

  if (location.pathname.startsWith("/control")) {
    return <ControlDeploymentRedirect />;
  }

  // Public landing page route `/`
  if (location.pathname === "/") {
    if (user) {
      return <Navigate to={role === "MEMBER" ? "/my-day" : "/cockpit"} replace />;
    }
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<PublicLandingPage />} />
        </Routes>
      </Suspense>
    );
  }

  // /login is the only un-gated app route.
  if (location.pathname === "/login") {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
        </Routes>
      </Suspense>
    );
  }

  // /accept-invite is an un-gated app route for accepting firm invites.
  if (location.pathname === "/accept-invite") {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
        </Routes>
      </Suspense>
    );
  }

  // Sprint 12.6 — signed-in users without a firmId claim are routed to the
  // firm-bootstrap onboarding flow instead of being silently signed out.
  if (user && needsFirmBootstrap) {
    if (location.pathname === "/onboarding/firm-bootstrap") {
      return (
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/onboarding/firm-bootstrap" element={<FirmBootstrapPage />} />
          </Routes>
        </Suspense>
      );
    }
    return <Navigate to="/onboarding/firm-bootstrap" replace />;
  }

  // No user → force /login, remembering where they were trying to go.
  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: { pathname: location.pathname + location.search } }}
      />
    );
  }

  if (!firmReady) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0f172a] text-xs font-bold text-slate-400 font-mono tracking-widest uppercase">
        Resolving firm access...
      </div>
    );
  }

  // Wave O / O-2 — WorkspaceGuard. A provisioned firm (has firmId) that has not
  // completed Gate-1 setup is workspace-ready=false: it may only reach the setup
  // wizard (and minimal recovery routes). This is the UI half of the gate; the
  // server enforces it too (finalizeFirmSetup + firmStatusGuard). See §3.3.
  if (user && firmReady && !setupComplete) {
    const onSetup = location.pathname.startsWith("/onboarding/firm-setup");
    const recoveryPath = location.pathname === "/support" || location.pathname === "/logout";
    if (onSetup) {
      return (
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/onboarding/firm-setup" element={<FirmSetupWizardPage />} />
            <Route path="*" element={<Navigate to="/onboarding/firm-setup" replace />} />
          </Routes>
        </Suspense>
      );
    }
    if (!recoveryPath) {
      return <Navigate to="/onboarding/firm-setup" replace />;
    }
  }

  const subscriptionAccess = resolveSubscriptionAccess({
    subscriptionStatus,
    trialExpiresAt,
    firmStatus,
    firmSuspended,
  });

  if (!subscriptionAccess.allowed && !isBillingRecoveryPath(location.pathname)) {
    return <Navigate to={`/settings${billingRecoverySearch(subscriptionAccess)}`} replace />;
  }

  if (!subscriptionAccess.allowed && location.pathname === "/billing") {
    return <SubscriptionRecoveryNotice statusLabel={subscriptionAccess.statusLabel} />;
  }

  return (
    <Shell>
      {/* 2. Main Content (Central Routing Zone) */}
      <main ref={shellChromeRef} className="flex-1 flex flex-col bg-[#F4F4F6] overflow-hidden" dir={isAr ? "rtl" : "ltr"}>
        {/* Beautiful persistent Layout Control Bar */}
        <div
          className={`bg-white/95 backdrop-blur-md rounded-2xl border border-slate-200/50 shadow-[0_4px_20px_rgba(15,23,42,0.02)] shrink-0 select-none z-20 ${
            isStackTier
              ? "mx-2 mt-2 px-3 py-2 grid grid-cols-1 gap-2"
              : isCompactTier
                ? "mx-3 mt-3 px-3 py-2 grid grid-cols-[minmax(0,1fr)_auto] gap-2 items-center"
                : "h-14 mx-4 mt-4 px-4 grid grid-cols-3 items-center"
          }`}
        >
          <div className={`flex items-center gap-3 min-w-0 ${isAr ? "flex-row-reverse justify-end" : "flex-row"}`}>
            {/* Toggle Left Sidebar Button — desktop only (mobile/tablet use the drawer + bottom nav) */}
            <button
               onClick={() => setSidebarOpen(!sidebarOpen)}
               className="p-1.5 hover:bg-slate-100 rounded-xl text-slate-600 hover:text-slate-950 transition-all cursor-pointer hidden lg:flex items-center gap-1.5 text-[11px] font-sans font-semibold border border-transparent hover:border-slate-200"
               title={sidebarOpen ? "Collapse Navigation Sidebar" : "Expand Navigation Sidebar"}
               aria-label={sidebarOpen ? "Collapse navigation sidebar" : "Expand navigation sidebar"}
            >
              <ChevronLeft 
                size={14} 
                className={`transition-transform duration-300 ${sidebarOpen ? "" : "rotate-180 text-orange-600"}`}
              />
            </button>
            <span className="text-slate-250 hidden lg:inline">|</span>
            {/* Breadcrumb Display */}
            <div className="text-[10px] text-slate-400 font-mono flex items-center gap-1.5">
              <span className={`${isStackTier ? "hidden" : "hidden sm:inline"}`}>{isAr ? "نظام التشغيل i-AM" : "i-AM OS"}</span>
              <span className={`${isStackTier ? "hidden" : "hidden sm:inline"}`}>/</span>
              <span className="text-slate-900 font-bold text-[10.5px] font-sans tracking-wide uppercase truncate max-w-[160px]">
                {currentRoute === "/" 
                  ? (isAr ? "لوحة التحكم" : "COCKPIT") 
                  : (isAr 
                      ? (currentRoute.includes("cases") ? "ملف القضية" : currentRoute.includes("contracts") ? "ملف العقد" : "مساحة العمل")
                      : currentRoute.replace("/", "").replace("-", " ") || "COCKPIT")
                }
              </span>
            </div>
          </div>

          {/* Centered Global Search Bar */}
          <div
            className={`h-full items-center min-w-0 ${
              isStackTier
                ? "flex order-3"
                : isCompactTier
                  ? "hidden"
                  : "flex justify-center"
            }`}
          >
            <div className="w-full min-w-0">
              <GlobalSearch />
            </div>
          </div>

          <div
            className={`flex items-center ${isStackTier ? "gap-2" : "gap-4"} ${
              isStackTier
                ? isAr ? "order-2 justify-start" : "order-2 justify-end"
                : isCompactTier
                  ? isAr ? "justify-start" : "justify-end"
                  : isAr ? "flex-row justify-start" : "flex-row justify-end"
            }`}
          >
            {/* AI Counsel Width Range slider - Shown only if AI Counsel is open */}
            {aiCounselOpen && !isStackTier && !isCompactTier && (
              <div className={`hidden lg:flex items-center gap-2 text-[10px] text-slate-500 bg-slate-50 border border-slate-200/60 rounded-xl px-2.5 py-1 ${isAr ? "flex-row-reverse" : ""}`}>
                <span className="font-mono font-bold text-slate-400 uppercase tracking-wider">{isAr ? "عرض الذكاء الكلي:" : "AI RAIL:"}</span>
                <span className="font-mono text-[#0f172a] font-bold w-10 text-right">{aiCounselWidth}%</span>
                <input 
                  type="range"
                  min="20"
                  max="40"
                  value={aiCounselWidth}
                  onChange={(e) => setAiCounselWidth(Number(e.target.value))}
                  className="w-16 h-1 bg-slate-250 rounded-lg cursor-pointer accent-orange-600"
                  style={{ direction: "ltr" }}
                />
              </div>
            )}

            {/* Toggle AI Counsel Button */}
            <button
              onClick={() => setAiCounselOpen(!aiCounselOpen)}
              className={`p-1.5 hover:bg-slate-100 rounded-xl text-slate-650 hover:text-slate-900 transition-all cursor-pointer flex items-center gap-1.5 text-[11px] font-sans font-semibold border border-transparent hover:border-slate-200 ${isStackTier ? "ml-auto" : ""}`}
              title={aiCounselOpen ? "Hide AI Counsel Right Rail" : "Show AI Counsel Right Rail"}
              aria-label={aiCounselOpen ? "Hide AI Counsel Right Rail" : "Show AI Counsel Right Rail"}
            >
              <Bot size={13} className={aiCounselOpen ? "text-orange-600 animate-pulse" : "text-slate-400"} />
              <ChevronRight 
                size={14} 
                className={`transition-transform duration-300 ${aiCounselOpen ? "" : "rotate-180 text-orange-600"}`}
              />
            </button>
          </div>
        </div>

        {/* Dynamic Route Container */}
        {creditBanner && (
          <div className={`mx-4 mt-3 rounded-xl border px-4 py-3 text-sm font-semibold ${
            creditReadOnly
              ? "border-red-200 bg-red-50 text-red-800"
              : "border-orange-200 bg-orange-50 text-orange-800"
          }`}>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <span>{creditBanner}</span>
              <button
                type="button"
                onClick={() => navigateToBilling()}
                className="w-fit rounded-lg bg-white px-3 py-1.5 text-xs font-black text-slate-900 shadow-sm ring-1 ring-slate-200"
              >
                Open billing
              </button>
            </div>
          </div>
        )}
        <div className="flex-1 flex flex-col overflow-hidden relative">
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/cockpit" element={<CockpitPage />} />
              <Route path="/my-day" element={<MyDayPage />} />
              <Route path="/pipeline" element={<PipelineDashboardPage />} />
              <Route path="/pipeline/:leadId" element={<PipelineLeadDetailPage />} />
              <Route path="/owner-center" element={<OwnerCenterPage />} />
              <Route path="/owner-center/referrals" element={<OwnerCenterReferralsPage />} />
              <Route path="/dashboard" element={<Navigate to="/cockpit" replace />} />
              <Route path="/admin" element={<AdminControlTowerPage />} />
              <Route path="/admin/:adminSurface" element={<AdminControlTowerPage />} />
              <Route path="/admin/control-tower" element={<AdminControlTowerPage />} />
              <Route path="/admin/control-tower/settings" element={<AdminSettingsSectionAliasRedirect />} />
              <Route path="/admin/control-tower/settings/:section" element={<AdminControlTowerPage />} />
              <Route path="/admin/control-tower/:adminSurface" element={<AdminControlTowerPage />} />
              <Route path="/control-tower" element={<Navigate to="/admin/control-tower" replace />} />
              <Route path="/control-tower/settings" element={<AdminSettingsSectionAliasRedirect />} />
              <Route path="/control-tower/settings/:section" element={<AdminSettingsSectionAliasRedirect />} />
              <Route path="/control-tower/:adminSurface" element={<AdminSurfaceAliasRedirect />} />
              <Route path="/super-admin" element={<Navigate to="/admin/control-tower" replace />} />
              <Route path="/super-admin/settings" element={<AdminSettingsSectionAliasRedirect />} />
              <Route path="/super-admin/settings/:section" element={<AdminSettingsSectionAliasRedirect />} />
              <Route path="/super-admin/:adminSurface" element={<AdminSurfaceAliasRedirect />} />
              <Route path="/system-admin" element={<Navigate to="/admin/control-tower" replace />} />
              <Route path="/system-admin/settings" element={<AdminSettingsSectionAliasRedirect />} />
              <Route path="/system-admin/settings/:section" element={<AdminSettingsSectionAliasRedirect />} />
              <Route path="/system-admin/:adminSurface" element={<AdminSurfaceAliasRedirect />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/alerts" element={<AlertsPage />} />
              <Route path="/parties" element={<AuthorizedPartiesPage />} />
              <Route path="/parties/" element={<AuthorizedPartiesPage />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/clients/new" element={<ClientsPage />} />
              <Route path="/clients/:clientId" element={<ClientWorkspacePage />} />
              <Route path="/portal-ops" element={<PortalOperationsPage />} />
              <Route path="/portal-health" element={<Navigate to="/portal-ops" replace />} />
              <Route path="/team" element={<TeamManagementAliasPage />} />
              <Route path="/work" element={<Navigate to="/work-queue" replace />} />
              <Route path="/agent-inbox" element={<Navigate to="/agent-workspace" replace />} />
              <Route path="/invoices" element={<Navigate to="/billing" replace />} />
              <Route path="/timesheet" element={<Navigate to="/time-tracking" replace />} />
              <Route path="/revenue" element={<Navigate to="/analytics" replace />} />
              <Route path="/ai-usage" element={<Navigate to="/analytics" replace />} />
              <Route path="/library" element={<Navigate to="/clause-library" replace />} />
              <Route path="/knowledge" element={<KnowledgeAliasPage />} />
              <Route path="/drafting" element={<DraftingPage />} />
              <Route path="/drafting/:draftId" element={<DraftingPage />} />
              <Route path="/agent-workspace" element={<AgentWorkspacePage />} />
              <Route path="/clause-library" element={<ClauseLibraryPage />} />
              <Route path="/templates" element={<DocumentTemplatesPage />} />
              <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
              <Route path="/tasks" element={<TasksPage />} />
              <Route path="/work-queue" element={<WorkQueuePage />} />
              <Route path="/time-tracking" element={<TimeTimerPage />} />
              <Route path="/exports" element={<ExportsLibraryPage />} />
              <Route path="/billing" element={<BillingLedgerPage />} />
              <Route path="/analytics" element={<FirmAnalyticsPage />} />
              <Route path="/search" element={<FacetedSearchPage />} />
              <Route path="/communications" element={<ClientCommsFeedPage />} />
              <Route path="/documents" element={<DocumentStoragePage />} />
              <Route path="/trash" element={<TrashRecoveryPage />} />
              <Route path="/settings" element={<FirmSettingsPage />} />
              <Route path="/settings/import" element={<SettingsImportPage />} />
              <Route path="/settings/microsoft/callback" element={<MicrosoftOAuthCallbackPage />} />
              <Route path="/settings/:settingsTab" element={<FirmSettingsPage />} />
              <Route path="/cases" element={<CasesWorkspaceIndexPage />} />
              <Route path="/matters" element={<Navigate to="/cases" replace />} />
              <Route path="/cases/new" element={<CasesWorkspaceIndexPage />} />
              <Route path="/contracts" element={<ContractsWorkspaceIndexPage />} />
              <Route path="/contracts/new" element={<ContractsWorkspaceIndexPage />} />
              <Route path="/cases/:caseId" element={<CasePage />} />
              <Route path="/contracts/:contractId" element={<ContractPage />} />
              <Route path="/" element={<Navigate to={role === "MEMBER" ? "/my-day" : "/cockpit"} replace />} />
              <Route path="*" element={<Navigate to="/cockpit" replace />} />
            </Routes>
          </Suspense>
        </div>
      </main>

      {/* 3. AI Counsel Right Rail (Static Right Zone) */}
      <PersistentChatPanel aiReadOnly={creditReadOnly} aiReadOnlyReason={creditBanner ?? undefined} />
    </Shell>
  );
};

export default function App() {
  return (
    <AuthProvider>
      <FirmProvider>
        <AppStateProvider>
          <AppContent />
        </AppStateProvider>
      </FirmProvider>
    </AuthProvider>
  );
}
